import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useCreateOrganization } from '../api/organization/organizationQueries.js';
import AuthLayout from '../components/auth/AuthLayout';
import SignUpForm from '../components/auth/SignUpForm';
import SignupSuccessDialog from '../components/auth/SignupSuccessDialog';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import useAuth from '../hooks/useAuth.js';

export default function SchoolSignUp() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const { loginLoading } = useAuth();

  // Use React Query mutation
  const { mutate: createOrganization } = useCreateOrganization();

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Pass the form data to createOrganization mutation with callbacks
      createOrganization(
        {
          country:
            formData.country.charAt(0).toUpperCase() +
            formData.country.slice(1),
          organizationName: formData.organizationName,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          dob: formData.dob,
        },
        {
          onSuccess: (response) => {
            // Ensure we have the correct data structure
            const data = response?.data || response;
            if (!data) {
              throw new Error('Invalid response from server');
            }

            setUserData({
              email: formData.email,
              organizationName: formData.organizationName,
              name: `${formData.firstName} ${formData.lastName}`,
              org: data.organizationId || data._id || data.id, // Handle all possible ID field names
              roleName: 'Administrator',
            });
            setIsSubmitting(false);
          },
          onError: (error) => {
            handleError(error, formData.email);
          },
        }
      );
    } catch (error) {
      handleError(error, formData.email);
    }
  };

  const handleError = (error, email) => {
    if (error.message === 'DUPLICATE_USER') {
      toast.error(
        'An account with this email already exists. Please try logging in.'
      );
      navigate('/login', { state: { email } });
      return;
    }

    setError(error.message);
    toast.error(error.message || 'Failed to create account. Please try again.');
    setIsSubmitting(false);
  };

  const handleDashboardRedirect = () => {
    // Navigate to login instead of attempting to login directly
    navigate('/login', {
      state: {
        email: userData.email,
        message: 'Account created successfully! Please log in to continue.',
      },
    });
  };

  const handleVerifyEmail = () => {
    navigate('/otp', {
      state: {
        email: userData.email,
        organizationId: userData.org,
      },
    });
  };

  if (loginLoading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <>
      <AuthLayout currentStep={1}>
        <div className="bg-white rounded-xl shadow-lg p-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Let's Get Started
          </h1>
          <p className="text-gray-500 mb-8">
            Already have an account?{' '}
            <Link to="/login" className="text-purple-600 hover:text-purple-700">
              Log in
            </Link>
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">{error}</span>
              </div>
            </div>
          )}

          <SignUpForm onSubmit={handleSubmit} isLoading={isSubmitting} />
        </div>
      </AuthLayout>

      {/* Only show success dialog when userData exists */}
      {userData && (
        <SignupSuccessDialog
          userData={userData}
          onDashboard={handleDashboardRedirect}
          onVerifyEmail={handleVerifyEmail}
        />
      )}
    </>
  );
}
